/**
 * AiAnalysisPromptsPage — Platform admin → AI Analysis Prompts.
 *
 * Centrally managed AI prompt architecture for SmartImprove (today) and
 * SmartEvaluate / SmartOperate / future PRIVA workflows (later). Prompts
 * are versioned per `prompt_key`; only ONE version per key can be Active
 * at a time. Activating a version automatically archives any prior
 * Active sibling (single-active invariant).
 *
 * No AI model is called from this page — this is the storage + admin-UX
 * layer only. Future backend services will load the active prompt by key.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  ArrowLeft, Bot, Save, Plus, Copy, CheckCircle2, Archive, Pencil,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";

const ax = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/admin`,
  withCredentials: true,
});

const STATUS_OPTIONS = ["Draft", "Active", "Archived"];
const MODULE_OPTIONS = ["SmartEvaluate", "SmartBid", "SmartImprove", "SmartEWM", "SmartOperate", "PRIVA", "Shared"];
const MODEL_OPTIONS = ["Claude", "GPT", "Gemini"];

export default function AiAnalysisPromptsPage({ application = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {mode:'edit'|'create', record?}

  const reload = async () => {
    setLoading(true);
    try {
      const r = await ax.get("/ai-analysis-prompts", {
        params: application ? { module: application } : {},
      });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load prompts");
    } finally {
      setLoading(false);
    }
  };

  // Load (and reload when the owning application changes). Single shared
  // repository; scope to the owning application when mounted inside a product
  // section (SmartEvaluate / SmartBid / SmartImprove). No `application` =>
  // legacy view of all prompts.
  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const r = await ax.get("/ai-analysis-prompts", {
          params: application ? { module: application } : {},
        });
        if (active) setRows(r.data || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load prompts");
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [application]);

  // Group rows by prompt_key so versioning is visually obvious.
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const k = r.prompt_key || "—";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    // Sort within group: Active first, then by version desc (v10, v9, …, v1)
    const versionNum = (v) => {
      const s = String(v || "").trim().toLowerCase();
      if (s.startsWith("v")) {
        const n = parseInt(s.slice(1), 10);
        if (!Number.isNaN(n)) return n;
      }
      return 0;
    };
    for (const arr of map.values()) {
      arr.sort((a, b) => {
        if (a.status === "Active" && b.status !== "Active") return -1;
        if (b.status === "Active" && a.status !== "Active") return 1;
        return versionNum(b.version) - versionNum(a.version);
      });
    }
    return Array.from(map.entries());
  }, [rows]);

  const onDuplicate = async (record) => {
    try {
      await ax.post(`/ai-analysis-prompts/${record.id}/duplicate`);
      toast.success("New draft version created");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to duplicate");
    }
  };

  const onActivate = async (record) => {
    try {
      await ax.post(`/ai-analysis-prompts/${record.id}/activate`);
      toast.success(`${record.version} is now Active`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to activate");
    }
  };

  const onArchive = async (record) => {
    try {
      await ax.post(`/ai-analysis-prompts/${record.id}/archive`);
      toast.success("Version archived");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to archive");
    }
  };

  if (loading) {
    return <div className="text-sm text-[#6B7280] p-6">Loading AI analysis prompts…</div>;
  }

  if (editing) {
    return (
      <PromptEditor
        mode={editing.mode}
        record={editing.record}
        onCancel={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await reload();
          toast.success("Prompt saved");
        }}
      />
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="ai-analysis-prompts-page">
      <header className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#6E5BCE] flex items-center gap-1">
            <Bot className="w-3 h-3" /> {application ? `${application} · AI` : "Platform · Reference Models"}
          </p>
          <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>
            AI Analysis Prompts
          </h1>
          <p className="text-[11px] text-[#6B7280] mt-0.5 max-w-3xl">
            {application
              ? `AI prompts owned by ${application}. Managed centrally in the shared prompt repository and versioned by `
              : "Centrally managed AI prompt architecture used by SmartImprove, SmartEvaluate, SmartBid, and PRIVA workflows. Each prompt is versioned by "}
            <strong>Prompt Key</strong>; only one version per key can be{" "}
            <strong>Active</strong> at a time.
          </p>
        </div>
        <Button
          onClick={() => setEditing({ mode: "create", record: application ? { module: application } : undefined })}
          className="bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs"
          data-testid="ai-prompt-new-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> New Prompt
        </Button>
      </header>

      <section className="grid grid-cols-1 gap-4" data-testid="ai-prompt-groups">
        {groups.length === 0 ? (
          <div className="bg-[#FFF7ED] border border-[#FED7AA] text-[#9A3412] text-xs rounded-sm px-4 py-3">
            No AI analysis prompts yet.
          </div>
        ) : null}
        {groups.map(([key, versions]) => (
          <PromptGroupCard
            key={key}
            promptKey={key}
            versions={versions}
            onEdit={(r) => setEditing({ mode: "edit", record: r })}
            onDuplicate={onDuplicate}
            onActivate={onActivate}
            onArchive={onArchive}
          />
        ))}
      </section>
    </div>
  );
}

function PromptGroupCard({ promptKey, versions, onEdit, onDuplicate, onActivate, onArchive }) {
  const head = versions[0] || {};
  return (
    <article
      data-testid={`ai-prompt-group-${promptKey}`}
      className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm"
    >
      <header className="px-4 py-3 border-b border-[#E5E7EB] flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5 mb-1">
            <ModuleBadge module={head.module} />
            <KeyBadge promptKey={promptKey} />
          </div>
          <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>
            {head.name || "Untitled Prompt"}
          </h3>
          {head.prompt_type ? (
            <p className="text-[11px] text-[#6B7280] mt-0.5">
              Type: <span className="font-medium text-[#374151]">{head.prompt_type}</span>
            </p>
          ) : null}
        </div>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid={`ai-prompt-versions-table-${promptKey}`}>
          <thead className="bg-[#F8FAFC] text-left text-[10px] uppercase tracking-wider text-[#6B7280]">
            <tr>
              <th className="px-4 py-2 font-semibold w-20">Version</th>
              <th className="px-4 py-2 font-semibold w-24">Status</th>
              <th className="px-4 py-2 font-semibold">Models</th>
              <th className="px-4 py-2 font-semibold w-48">Last Updated</th>
              <th className="px-4 py-2 font-semibold w-40">Updated By</th>
              <th className="px-4 py-2 font-semibold text-right w-72">Actions</th>
            </tr>
          </thead>
          <tbody>
            {versions.map((v) => (
              <tr key={v.id} className="border-t border-[#F1F5F9]" data-testid={`ai-prompt-row-${v.id}`}>
                <td className="px-4 py-2 font-semibold text-[#111827]">{v.version}</td>
                <td className="px-4 py-2"><StatusBadge status={v.status} /></td>
                <td className="px-4 py-2 text-[#6B7280]">
                  {(v.model_compatibility || []).join(" / ") || "—"}
                </td>
                <td className="px-4 py-2 text-[#6B7280]">{formatDate(v.updated_at)}</td>
                <td className="px-4 py-2 text-[#6B7280]">{v.updated_by || "—"}</td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    <Button
                      variant="outline"
                      className="rounded-sm h-7 text-[11px]"
                      onClick={() => onEdit(v)}
                      data-testid={`ai-prompt-edit-${v.id}`}
                    >
                      <Pencil className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button
                      variant="outline"
                      className="rounded-sm h-7 text-[11px]"
                      onClick={() => onDuplicate(v)}
                      data-testid={`ai-prompt-duplicate-${v.id}`}
                    >
                      <Copy className="w-3 h-3 mr-1" /> New Version
                    </Button>
                    {v.status !== "Active" ? (
                      <Button
                        className="rounded-sm h-7 text-[11px] bg-[#6E5BCE] hover:bg-[#5847B0] text-white"
                        onClick={() => onActivate(v)}
                        data-testid={`ai-prompt-activate-${v.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Activate
                      </Button>
                    ) : null}
                    {v.status !== "Archived" && v.status !== "Active" ? (
                      <Button
                        variant="outline"
                        className="rounded-sm h-7 text-[11px]"
                        onClick={() => onArchive(v)}
                        data-testid={`ai-prompt-archive-${v.id}`}
                      >
                        <Archive className="w-3 h-3 mr-1" /> Archive
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </article>
  );
}

function StatusBadge({ status }) {
  const map = {
    Active:   { bg: "#F0EDF9", border: "#C8BFE3", text: "#473F8B" },
    Draft:    { bg: "#FEF3C7", border: "#FDE68A", text: "#92400E" },
    Archived: { bg: "#F3F4F6", border: "#E5E7EB", text: "#6B7280" },
  };
  const s = map[status] || map.Draft;
  return (
    <span
      className="inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border"
      style={{ background: s.bg, borderColor: s.border, color: s.text }}
      data-testid={`ai-prompt-status-${status}`}
    >
      {status || "Draft"}
    </span>
  );
}

function ModuleBadge({ module }) {
  return (
    <span
      className="text-[10px] font-bold tracking-wider text-[#025979] bg-[#F0F7FA] border border-[#BAE6FD] px-1.5 py-0.5 rounded-sm uppercase"
    >
      {module || "SmartImprove"}
    </span>
  );
}

function KeyBadge({ promptKey }) {
  return (
    <span className="text-[10px] font-mono text-[#6B7280] bg-[#F8FAFC] border border-[#E5E7EB] px-1.5 py-0.5 rounded-sm">
      {promptKey}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

// ---------------------------------------------------------------------------
// Editor (also handles Create)
// ---------------------------------------------------------------------------
function PromptEditor({ mode, record, onCancel, onSaved }) {
  const isCreate = mode === "create";
  const seed = record || {};
  const [name, setName] = useState(seed.name || "");
  const [promptKey, setPromptKey] = useState(seed.prompt_key || "");
  const [module, setModule] = useState(seed.module || "SmartImprove");
  const [promptType, setPromptType] = useState(seed.prompt_type || "");
  const [version, setVersion] = useState(seed.version || "");
  const [status, setStatus] = useState(seed.status || "Draft");
  const [modelCompat, setModelCompat] = useState(seed.model_compatibility || []);
  const [systemPrompt, setSystemPrompt] = useState(seed.system_prompt || "");
  const [schema, setSchema] = useState(seed.expected_output_schema || "");
  const [guardrails, setGuardrails] = useState(seed.guardrails || "");
  const [exampleInputs, setExampleInputs] = useState(seed.example_inputs || "");
  const [exampleOutputs, setExampleOutputs] = useState(seed.example_outputs || "");
  const [saving, setSaving] = useState(false);

  const toggleModel = (m) => {
    setModelCompat((prev) =>
      prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]
    );
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name is required"); return; }
    if (isCreate && !promptKey.trim()) { toast.error("Prompt Key is required"); return; }

    const payload = {
      name: name.trim(),
      module,
      prompt_type: promptType.trim(),
      status,
      model_compatibility: modelCompat,
      system_prompt: systemPrompt,
      expected_output_schema: schema,
      guardrails,
      example_inputs: exampleInputs,
      example_outputs: exampleOutputs,
    };
    if (isCreate) payload.prompt_key = promptKey.trim();
    if (version.trim()) payload.version = version.trim();

    setSaving(true);
    try {
      if (isCreate) {
        await ax.post("/ai-analysis-prompts", payload);
      } else {
        await ax.put(`/ai-analysis-prompts/${record.id}`, payload);
      }
      await onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="ai-prompt-editor">
      <button
        onClick={onCancel}
        className="text-xs text-[#6E5BCE] hover:text-[#5847B0] flex items-center gap-1 mb-4"
        data-testid="ai-prompt-editor-back"
      >
        <ArrowLeft className="w-3 h-3" /> Back to AI Analysis Prompts
      </button>

      <header className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#6E5BCE]">
            {isCreate ? "New Prompt" : "Edit Prompt"}
          </p>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Prompt name"
            className="rounded-sm text-lg font-bold border-transparent focus-visible:border-[#6E5BCE] focus-visible:ring-0 px-0 h-9"
            data-testid="ai-prompt-name"
          />
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#6E5BCE] hover:bg-[#5847B0] text-white rounded-sm text-xs"
          data-testid="ai-prompt-save"
        >
          <Save className="w-3 h-3 mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </header>

      <Section title="Identity">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Prompt Key" hint="Stable identifier used by backend services (immutable after creation).">
            <Input
              value={promptKey}
              onChange={(e) => setPromptKey(e.target.value)}
              disabled={!isCreate}
              placeholder="e.g. smartimprove_transformation_analysis"
              className="rounded-sm text-xs h-8 font-mono"
              data-testid="ai-prompt-key"
            />
          </Field>
          <Field label="Version" hint="Auto-incremented (vN) if left blank on create.">
            <Input
              value={version}
              onChange={(e) => setVersion(e.target.value)}
              placeholder="auto"
              className="rounded-sm text-xs h-8"
              data-testid="ai-prompt-version"
            />
          </Field>
          <Field label="Module">
            <select
              value={module}
              onChange={(e) => setModule(e.target.value)}
              className="w-full rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs bg-white focus:border-[#6E5BCE] focus:outline-none h-8"
              data-testid="ai-prompt-module"
            >
              {MODULE_OPTIONS.map((m) => (
                <option key={m} value={m}>{m}</option>
              ))}
            </select>
          </Field>
          <Field label="Prompt Type" hint="e.g. Transformation Analysis, Insight Generation, Executive Summary.">
            <Input
              value={promptType}
              onChange={(e) => setPromptType(e.target.value)}
              placeholder="Transformation Analysis"
              className="rounded-sm text-xs h-8"
              data-testid="ai-prompt-type"
            />
          </Field>
          <Field label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-sm border border-[#E5E7EB] px-2 py-1 text-xs bg-white focus:border-[#6E5BCE] focus:outline-none h-8"
              data-testid="ai-prompt-status-select"
            >
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Field>
          <Field label="Model Compatibility">
            <div className="flex flex-wrap gap-2 pt-1">
              {MODEL_OPTIONS.map((m) => {
                const on = modelCompat.includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => toggleModel(m)}
                    className={`text-[11px] font-medium px-2 py-1 rounded-sm border transition ${
                      on
                        ? "bg-[#F0EDF9] border-[#C8BFE3] text-[#473F8B]"
                        : "bg-white border-[#E5E7EB] text-[#6B7280] hover:border-[#C8BFE3]"
                    }`}
                    data-testid={`ai-prompt-model-${m.toLowerCase()}`}
                  >
                    {m}
                  </button>
                );
              })}
            </div>
          </Field>
        </div>
      </Section>

      <Section
        title="System Prompt"
        description="The persona / instructions handed to the model at the top of the conversation."
      >
        <TextArea
          value={systemPrompt}
          onChange={setSystemPrompt}
          rows={10}
          placeholder="You are an SAP EWM transformation consultant…"
          testid="ai-prompt-system"
        />
      </Section>

      <Section
        title="Expected Output Schema"
        description="JSON schema the model must return. The future parser will validate generations against this contract."
      >
        <TextArea
          value={schema}
          onChange={setSchema}
          rows={14}
          monospace
          placeholder='{ "finding_analysis": [], "solution_initiatives": [], … }'
          testid="ai-prompt-schema"
        />
      </Section>

      <Section
        title="Guardrails"
        description="Hard rules the model must obey. Renders inline at the end of the system prompt during generation."
      >
        <TextArea
          value={guardrails}
          onChange={setGuardrails}
          rows={8}
          placeholder="- Never invent findings.&#10;- Keep output as strict JSON only.&#10;…"
          testid="ai-prompt-guardrails"
        />
      </Section>

      <Section title="Example Inputs" description="Optional — few-shot example payload(s) for the model.">
        <TextArea
          value={exampleInputs}
          onChange={setExampleInputs}
          rows={8}
          monospace
          placeholder="Optional example input JSON…"
          testid="ai-prompt-examples-input"
        />
      </Section>

      <Section title="Example Outputs" description="Optional — paired example response(s) showing the exact JSON contract.">
        <TextArea
          value={exampleOutputs}
          onChange={setExampleOutputs}
          rows={8}
          monospace
          placeholder="Optional example output JSON…"
          testid="ai-prompt-examples-output"
        />
      </Section>
    </div>
  );
}

function Section({ title, description, children }) {
  return (
    <section className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm mb-5">
      <header className="px-5 py-3 border-b border-[#E5E7EB]">
        <h3 className="text-sm font-semibold text-[#111827]" style={{ fontFamily: "Chivo" }}>{title}</h3>
        {description ? <p className="text-[11px] text-[#6B7280] mt-0.5">{description}</p> : null}
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-semibold tracking-wider uppercase text-[#6B7280] block mb-1">
        {label}
      </span>
      {children}
      {hint ? <p className="text-[10px] text-[#9CA3AF] mt-1">{hint}</p> : null}
    </label>
  );
}

function TextArea({ value, onChange, rows = 6, monospace, placeholder, testid }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      rows={rows}
      placeholder={placeholder}
      className={`w-full rounded-sm border border-[#E5E7EB] px-3 py-2 text-xs focus:border-[#6E5BCE] focus:outline-none ${
        monospace ? "font-mono" : ""
      }`}
      data-testid={testid}
    />
  );
}
